import React, { useState, useEffect } from 'react';
import api from '../../api';
import AnimatedMapSprite from '../../Pages/Maps/logic/AnimimatedMapSprite';

const PromoModal = ({ 
  isOpen, 
  toggle, 
  spawnText,
  playerPos
}) => {
  // -- common state
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('promo');
  const [promoData, setPromoData] = useState(null);
  const [userCurrency, setUserCurrency] = useState({ money: 0, coins: 0 });
  const [currentTime, setCurrentTime] = useState(Date.now() / 1000);
  const [rewardTable, setRewardTable] = useState([]);
  const [verificationModal, setVerificationModal] = useState(false);
  const [verificationImage, setVerificationImage] = useState(null);
  const [verificationAnswer, setVerificationAnswer] = useState('');

  // Load promo data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPromoData();
      fetchCurrency();
    }
  }, [isOpen]);

  // Live countdown timer
  useEffect(() => {
    if (isOpen) {
      const timer = setInterval(() => {
        setCurrentTime(Date.now() / 1000);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isOpen]);

  // Build reward table when promoData arrives
  useEffect(() => {
    if (promoData && promoData.promo?.promo) {
      setRewardTable(generateRewardTable(promoData.promo.promo));
      const rewardInterval = setInterval(() => {
        setRewardTable(generateRewardTable(promoData.promo.promo));
      }, 1000);
      return () => clearInterval(rewardInterval);
    }
  }, [promoData]);

  const fetchPromoData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/promo', { withCredentials: true });
      setPromoData(response.data);
    } catch (err) {
      console.error('Error loading promo data:', err);
      if (spawnText && playerPos) {
        spawnText('Failed to load promo data', playerPos.x, playerPos.y - 20, '#f87171');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrency = async () => {
    try {
      const response = await api.get('/v1/currency', { withCredentials: true });
      const currencyData = Array.isArray(response.data) ? response.data[0] : response.data;
      setUserCurrency({
        money: currencyData.money || 0,
        coins: currencyData.coins || 0
      });
    } catch (err) {
      console.error('Error loading currency:', err);
    }
  };

  // Helper to compute time left
  const getTimeLeft = (endtime) => {
    const nowSec = Math.floor(Date.now() / 1000);
    const diff = Math.max(0, endtime - nowSec);

    const d = Math.floor(diff / (3600 * 24));
    const h = Math.floor((diff % (3600 * 24)) / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;

    return diff > 0 ? `${d}d ${h}h ${m}m ${s}s` : "Expired";
  };

  // Reward generation logic from promo-center
  const tier1Rewards = [
    { id: 214, name: "Lure x3", image: "lure.png", probability: "60%" },
    { id: 2222258, name: "Bronze Safe", image: "bronzesafe.png", probability: "60%" },
  ];
  
  const tier2Rewards = [
    { id: 701780, name: "Super Lure x2", image: "superlure.png", probability: "30%" },
    { id: 701781, name: "Ultra Lure", image: "ultralure.png", probability: "30%" },
    { id: 60, name: "Safe", image: "safe.png", probability: "30%" },
  ];

  const getColorBox = (pokemonName) => {
    if (pokemonName.includes("Diamond"))
      return { id: 181, name: "Diamond Box", image: "diamondbox.png" };
    if (pokemonName.includes("Shadow"))
      return { id: 182, name: "Shadow Box", image: "shadowbox.png" };
    if (pokemonName.includes("Golden"))
      return { id: 184, name: "Golden Box", image: "goldenbox.png" };
    return null;
  };

  const generateRewardTable = (pokemonName) => {
    const firstPlaceReward = getColorBox(pokemonName);
    const secondPlaceReward = tier1Rewards[Math.floor(Math.random() * tier1Rewards.length)];
    const thirdPlaceReward = tier2Rewards[Math.floor(Math.random() * tier2Rewards.length)];

    return [
      { place: "1st", reward: firstPlaceReward },
      { place: "2nd", reward: secondPlaceReward },
      { place: "3rd", reward: thirdPlaceReward },
    ];
  };

  const initiateClaimPromo = async () => {
    try {
      const response = await api.get('/v1/promo_verification', {
        withCredentials: true,
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });
      const imageUrl = URL.createObjectURL(response.data);
      setVerificationImage(imageUrl);
      // Reset previous answer each time verification opens
      setVerificationAnswer('');
      setVerificationModal(true);
    } catch (err) {
      console.error('Error loading verification:', err);
      if (spawnText && playerPos) {
        spawnText('Failed to load verification', playerPos.x, playerPos.y - 20, '#f87171');
      }
    }
  };

  const claimPromo = async () => {
    try {
      // Enforce single-character verification (letter)
      const ans = (verificationAnswer || '').trim();
      if (ans.length !== 1) {
        if (spawnText && playerPos) {
          spawnText("Please enter a single letter (e.g., 'P' for Pikachu)", playerPos.x, playerPos.y - 20, '#f59e0b');
        }
        return;
      }

      const formData = new URLSearchParams();
      formData.append('verification_answer', verificationAnswer);

      const response = await api.post('/v1/claim_promo', formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // Show success message and close modal
      if (spawnText && playerPos) {
        // Show promo Pokemon message
        spawnText(
          `+1 ${promoData.promo.promo}!`,
          playerPos.x,
          playerPos.y - 20,
          '#4ade80',
          `/sprites/${promoData.promo.promo}.png`
        );
        
        // If there's a reward, show it as well
        if (response.data && response.data.reward) {
          setTimeout(() => {
            // Ensure the image path is properly formatted
            const imagePath = response.data.reward.item_image ? 
              (response.data.reward.item_image.startsWith('/') ? 
                response.data.reward.item_image : 
                `/${response.data.reward.item_image}`
              ) : null;
              
            spawnText(
              `+${response.data.reward.amount} ${response.data.reward.item_name}!`,
              playerPos.x,
              playerPos.y - 40,
              '#f1c40f',
              imagePath
            );
          }, 1000); // Show reward message after a short delay
        }
      }

      setVerificationModal(false);
      toggle();

    } catch (err) {
      console.error('Error claiming promo:', err);
      if (spawnText && playerPos) {
        const errorMsg = err.response?.data?.detail || 'Claim failed';
        spawnText(errorMsg, playerPos.x, playerPos.y - 20, '#f87171');
      }
      setVerificationModal(false);
    }
  };

  // Check if user can claim promo
  const hasEnoughCurrency = promoData && 
    promoData.claimed?.promo === 0 &&
    promoData.claimed?.captcha_attempts <= 5 &&
    userCurrency.money >= (promoData.promo?.cost || 0) &&
    userCurrency.coins >= (promoData.promo?.credits || 0) &&
    Date.now() / 1000 < (promoData.promo?.promoends || 0);

  if (!isOpen) return null;

  return (
    <div 
      className="game-popup-overlay"
      onClick={toggle}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="promo-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 1200,
          maxHeight: 'calc(100vh - 40px)',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '10px'
        }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Promo Center</h3>
          </div>
          <button
            onClick={toggle}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              opacity: 0.7
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          {/* Tab Navigation */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveTab('promo')}
              style={{
                background: activeTab === 'promo' ? 'rgba(255, 193, 7, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                border: activeTab === 'promo' ? '1px solid rgba(255, 193, 7, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: activeTab === 'promo' ? 'bold' : 'normal',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'promo') {
                  e.target.style.background = 'rgba(108, 117, 125, 0.8)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'promo') {
                  e.target.style.background = 'rgba(108, 117, 125, 0.6)';
                }
              }}
            >
            Promo
            </button>
            <button
              onClick={() => setActiveTab('map-promo')}
              style={{
                background: activeTab === 'map-promo' ? 'rgba(255, 193, 7, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                border: activeTab === 'map-promo' ? '1px solid rgba(255, 193, 7, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: activeTab === 'map-promo' ? 'bold' : 'normal',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'map-promo') {
                  e.target.style.background = 'rgba(108, 117, 125, 0.8)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'map-promo') {
                  e.target.style.background = 'rgba(108, 117, 125, 0.6)';
                }
              }}
            >
              Map Promo
            </button>
          </div>

          {loading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              fontSize: '16px', 
              opacity: 0.8 
            }}>
              Loading promo data...
            </div>
          ) : (
            <>
              {/* Promo Tab */}
              {activeTab === 'promo' && promoData && (
                <div>
                  {/* Rules Section */}
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <h4 style={{ color: '#e74c3c', marginBottom: '12px', fontSize: '14px' }}>Rules:</h4>
                    <ul style={{ color: '#ccc', fontSize: '12px', margin: 0, paddingLeft: '20px' }}>
                      <li>Please do not claim more than two of the same promo per household/IP address</li>
                      <li>Spamming the forums may result in a ban and loss of your promo</li>
                      <li>Do not use multiple accounts per IP to grab more than the first three promos</li>
                    </ul>
                  </div>

                  {/* Main Promo Card */}
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    marginBottom: '20px'
                  }}>
                    {/* Promo Image */}
                    <img
                      src={`/sprites/${promoData.promo?.promo}.png`}
                      alt={promoData.promo?.promo}
                      style={{ width: 120, marginBottom: 16 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />

                    {/* Promo Name */}
                    <h3 style={{ color: '#fff', fontWeight: 'bold', marginBottom: 16 }}>
                      {promoData.promo?.promo}
                    </h3>

                    {/* Cost Display */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      gap: '20px', 
                      marginBottom: 16,
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <img src="/items/money.png" alt="money" width={20} />
                        <span style={{ color: '#ccc' }}>
                          {(promoData.promo?.cost || 0).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <img src="/items/coin.png" alt="coins" width={20} />
                        <span style={{ color: '#ccc' }}>
                          {(promoData.promo?.credits || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Countdown */}
                    <div style={{
                      fontWeight: 'bold',
                      color: (promoData.promo?.promoends || 0) / 1000 - currentTime < 3600 ? '#f87171' : '#4ade80',
                      marginBottom: 20,
                      fontSize: '14px'
                    }}>
                      Ends in {getTimeLeft(promoData.promo?.promoends || 0)}
                    </div>

                    {/* Claim Button */}
                    <button
                      onClick={initiateClaimPromo}
                      disabled={!hasEnoughCurrency}
                      style={{
                        background: hasEnoughCurrency ? 'rgba(220, 53, 69, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                        border: hasEnoughCurrency ? '1px solid rgba(220, 53, 69, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                        color: '#fff',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        cursor: hasEnoughCurrency ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease',
                        opacity: hasEnoughCurrency ? 1 : 0.6
                      }}
                      onMouseEnter={(e) => {
                        if (hasEnoughCurrency) {
                          e.target.style.background = 'rgba(220, 53, 69, 1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (hasEnoughCurrency) {
                          e.target.style.background = 'rgba(220, 53, 69, 0.8)';
                        }
                      }}
                    >
                      {hasEnoughCurrency ? 'Claim Promo' : 'Cannot Claim'}
                    </button>
                  </div>

                  {/* Reward Table */}
                  {rewardTable.length > 0 && (
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <h4 style={{ color: '#f1c40f', marginBottom: '12px', fontSize: '14px' }}>
                        Promo Prizes (First 3 Players):
                      </h4>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {rewardTable.map((entry, idx) => (
                          <div key={idx} style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            textAlign: 'center',
                            minWidth: '120px'
                          }}>
                            <div style={{ color: '#ffc107', fontWeight: 'bold', fontSize: '12px' }}>
                              {entry.place} Place
                            </div>
                            {entry.reward && (
                              <>
                                <img
                                  src={`/items/${entry.reward.image}`}
                                  alt={entry.reward.name}
                                  width={32}
                                  style={{ margin: '4px 0' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <div style={{ color: '#ccc', fontSize: '10px' }}>
                                  {entry.reward.name}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Map Promo Tab */}
              {activeTab === 'map-promo' && promoData && (
                <div>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <h4 style={{ color: '#3498db', marginBottom: '8px', fontSize: '14px' }}>Map Promos:</h4>
                    <p style={{ color: '#ccc', fontSize: '12px', margin: 0 }}>
                      Map Promos are separate from regular promos and can be found by exploring any of the maps. 
                      Using more than 2 accounts to claim map promos is <strong>bannable</strong>.
                    </p>
                  </div>

                  {/* Map Promo Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                    {promoData.event_maps && ['poke1', 'poke2', 'poke3'].map((key, idx) => {
                      const name = promoData.event_maps[key];
                      if (!name) return null;
                      
                      const spawn = promoData.event_maps[`spawn_rate${idx + 1}`];
                      const maxRarity = promoData.event_maps[`max_rarirty${idx + 1}`] || "∞";
                      const caught = promoData.user_event_captures?.[`event_mon${idx + 1}`] || 0;
                      const limit = promoData.event_maps[`limit_${idx + 1}`];

                      return (
                        <div key={key} style={{
                          background: 'rgba(0, 0, 0, 0.3)',
                          padding: '16px',
                          borderRadius: '8px',
                          textAlign: 'center',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <h5 style={{ color: '#f1c40f', marginBottom: '12px', fontSize: '12px' }}>
                            Map Promo
                          </h5>
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: '8px',
                            marginBottom: '8px' 
                          }}>
                            <img
                              src={`/sprites/${name}.png`}
                              alt={name}
                              width={80}
                              height={80}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <span style={{ color: '#ccc', fontSize: '10px' }}>Map Sprite:</span>
                              <AnimatedMapSprite
                                pokeName={name}
                                style={{ width: 32, height: 32 }}
                              />
                            </div>
                          </div>
                          <h6 style={{ color: '#fff', margin: '8px 0', fontSize: '14px' }}>
                            {name}
                          </h6>
                          <div style={{ color: '#ccc', fontSize: '11px', marginBottom: '4px' }}>
                            Spawn: {((spawn || 0) / 1500 * 100).toFixed(2)}%
                          </div>
                          <div style={{ color: '#ccc', fontSize: '11px', marginBottom: '4px' }}>
                            Max Rarity: {maxRarity}
                          </div>
                          <div style={{ color: '#ccc', fontSize: '11px' }}>
                            Caught: {caught}/{limit || '∞'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Verification Modal */}
      {verificationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'rgba(29, 34, 46, 0.95)',
            padding: '20px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <h4 style={{ color: '#fff', marginBottom: '16px' }}>Verification Required</h4>
            {verificationImage && (
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <img 
                  src={verificationImage} 
                  alt="Verification" 
                  style={{ 
                    maxWidth: '100%', 
                    borderRadius: '4px',
                    animation: 'rotateAndFloat 3s ease-in-out infinite',
                    transformOrigin: 'center'
                  }}
                />
              </div>
            )}
            <input
              type="text"
              value={verificationAnswer}
              onChange={(e) => {
                // Allow any single character (including w,a,s,d keys)
                const val = e.target.value.slice(0, 1).toUpperCase();
                setVerificationAnswer(val);
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                // Prevent game movement when typing w,a,s,d
                e.stopPropagation();
              }}
              placeholder="Enter a single character"
              maxLength={1}
              title="Enter a single character"
              style={{
                width: '100%',
                padding: '8px 12px',
                marginBottom: '8px',
                borderRadius: '4px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#fff',
                fontSize: '14px'
              }}
            />
            <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '16px' }}>
              Answer is a single letter. Example: enter <strong>P</strong> for Pikachu.
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button
                onClick={claimPromo}
                disabled={(verificationAnswer || '').trim().length !== 1}
                style={{
                  background: ((verificationAnswer || '').trim().length === 1) ? 'rgba(40, 167, 69, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                  border: ((verificationAnswer || '').trim().length === 1) ? '1px solid rgba(40, 167, 69, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: ((verificationAnswer || '').trim().length === 1) ? 'pointer' : 'not-allowed',
                  fontSize: '12px'
                }}
              >
                Claim
              </button>
              <button
                onClick={() => setVerificationModal(false)}
                style={{
                  background: 'rgba(108, 117, 125, 0.8)',
                  border: '1px solid rgba(108, 117, 125, 0.8)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoModal;
